<template lang="pug">
#emergency-transport-patient-detail(v-cloak)
  .box
    .box-header.with-border.register-header
      .title
        span 救急搬送情報 詳細

    .box-body.register-body
      //- 1. 送信元・搬送先情報
      FormLabel(key-name="送信元施設名" :value="patient ? patient.facilityName : ''")
      FormLabel(key-name="送信元施設住所" :value="patient ? patient.facilityAddress : ''")
      FormLabel(key-name="送信元施設電話番号" :value="patient ? patient.facilityTel : ''")
      FormLabel(key-name="搬送先病院名" :value="patient ? patient.targetFacilityName : ''")
      FormLabel(key-name="担当職員" :value="patient ? patient.assignedStaffName : ''")

      //- 2. 患者・利用者情報
      FormLabel(key-name="氏名" :value="patient ? patient.name : ''")
      FormLabel(key-name="生年月日" :value="_birthdayText")
      FormLabel(key-name="性別" :value="patient ? patient.gender : ''")
      FormLabel(key-name="身長" :value="patient && patient.height ? patient.height + ' cm' : ''")
      FormLabel(key-name="体重" :value="patient && patient.weight ? patient.weight + ' kg' : ''")
      FormLabel(key-name="血液型" :value="patient ? patient.bloodType : ''")

      //- 3. 家族情報
      .register-row.form-label
        .register-group
          .register-key
            span.key-name 家族
          .register-value.d-flex.flex-column.align-items-start.w-100
            template(v-if="patient && patient.families && patient.families.length > 0")
              .register-card-box.w-100.mb-2(v-for="(family, index) in patient.families" :key="index")
                .card-body
                  FormLabel(key-name="氏名" :value="family.name")
                  FormLabel(key-name="電話番号" :value="family.tel")
            template(v-else)
              span.error-message 未設定

      //- 4. かかりつけ医
      .register-row.form-label
        .register-group
          .register-key
            span.key-name かかりつけ医
          .register-value.d-flex.flex-column.align-items-start.w-100
            .register-card-box.w-100.mb-2
              .card-body
                FormLabel(key-name="病院名" :value="patient ? patient.primaryHospitalName : ''")
                FormLabel(key-name="担当医" :value="patient ? patient.primaryDoctorName : ''")
                FormLabel(key-name="電話番号" :value="patient ? patient.primaryHospitalTel : ''")

      //- 5. 救急搬送の経緯
      FormLabel(key-name="救急搬送の経緯" :value="patient ? patient.note : ''")

      //- 6. 最終バイタルサイン
      .register-row.form-label
        .register-group
          .register-key
            span.key-name 最終バイタルサイン
          .register-value.d-flex.flex-column.align-items-start.w-100
            .register-card-box.w-100.mb-2
              .card-body
                FormLabel(key-name="血圧" :value="_bloodPressureText")
                FormLabel(key-name="脈拍" :value="_pulseText")
                FormLabel(key-name="呼吸" :value="_respirationText")
                FormLabel(key-name="体温" :value="_bodyTemperatureText")
                FormLabel(key-name="SpO2" :value="_spo2Text")
                FormLabel(key-name="特記事項" :value="patient ? patient.specialNotes : ''")

      //- 7. 医療・連絡事項
      FormLabel(key-name="禁忌・アレルギー" :value="patient ? patient.allergiesAndContraindications : ''")
      FormLabel(key-name="既往歴" :value="patient ? patient.medicalHistory : ''")
      FormLabel(key-name="服用中の薬" :value="patient ? patient.medications : ''")
      FormLabel(key-name="その他の連絡事項" :value="patient ? patient.otherInfo : ''")

    .box-footer.with-border.register-footer
      .buttons.d-flex.flex-row.justify-content-end
        button.btn.print-btn(type="button" @click="onPrint" :disabled="!patient") 印刷
        button.btn.cancel-btn(type="button" @click="gotoList") 戻る

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore, fontsStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";

import { PcUrl } from "@/commons/constants/PcUrl";
import EmergencyTransportPatientsService, { EmergencyTransportPatientModel } from "@/commons/services/EmergencyTransportPatients";
import { EmergencyTransportPatientPdf } from "@/commons/utils/EmergencyTransportPatientPdf";

import PageLoading from "@/pc/basics/PageLoading.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";

/**
 * 救急搬送情報詳細画面コンポーネント（送信側・受信側共通）
 */
@Component({
  components: {
    PageLoading,
    FormLabel
  }
})
export default class EmergencyTransportPatientDetail extends Vue {
  /** 救急搬送サービス */
  @Inject("EmergencyTransportPatientsService") emergencyTransportPatientsService!: EmergencyTransportPatientsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** 対象患者情報 */
  patient: EmergencyTransportPatientModel | null = null;

  /** ### 生年月日フォーマット */
  get _birthdayText(): string {
    if (!this.patient || !this.patient.birthday) return "";
    return this.$dateUtil.convertNumberToString(this.patient.birthday, "yyyy年mm月dd日", false);
  }

  /** ### 血圧フォーマット */
  get _bloodPressureText(): string {
    if (!this.patient) return "";
    const max = this.patient.maxBloodPressure;
    const min = this.patient.minBloodPressure;
    if (max != null && min != null) {
      return `${max} / ${min} mmHg`;
    } else if (max != null) {
      return `最高: ${max} mmHg`;
    } else if (min != null) {
      return `最低: ${min} mmHg`;
    }
    return "";
  }

  /** ### 脈拍フォーマット */
  get _pulseText(): string {
    if (!this.patient || this.patient.pulse == null) return "";
    return `${this.patient.pulse} 回/分`;
  }

  /** ### 呼吸フォーマット */
  get _respirationText(): string {
    if (!this.patient || this.patient.respiration == null) return "";
    return `${this.patient.respiration} 回/分`;
  }

  /** ### 体温フォーマット */
  get _bodyTemperatureText(): string {
    if (!this.patient || !this.patient.bodyTemperature) return "";
    return `${this.patient.bodyTemperature} ℃`;
  }

  /** ### SpO2フォーマット */
  get _spo2Text(): string {
    if (!this.patient || this.patient.spo2 == null) return "";
    return `${this.patient.spo2} %`;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面表示情報作成
   */
  public async createView(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "読込み中...";

    try {
      const id = Number(this.$route.params.id);
      const targetFacilityId = this.$route.query.targetFacilityId ? Number(this.$route.query.targetFacilityId) : undefined;

      const patient = await this.emergencyTransportPatientsService.get(userStore, id, targetFacilityId ? { targetFacilityId } : undefined);
      this.patient = patient;
    } catch (error) {
      errorHandle(error, this);
    } finally {
      this.isBusy = false;
    }

    return;
  }

  /**
   * ### 一覧画面へ遷移する
   */
  public gotoList(): void {
    this.$router.push(PcUrl.OtherOfficeEmergencyTransports);
    return;
  }

  /**
   * ### 印刷処理（PDF出力）
   */
  public async onPrint(): Promise<void> {
    if (!this.patient) return;
    this.isBusy = true;
    this.loadingMessage = "PDFを作成中...";

    try {
      const font: string = await fontsStore.getPdfFont({ userStore });
      const pdfUtil = new EmergencyTransportPatientPdf(font);
      const dateStr = this.$dateUtil.convertNumberToString(this.patient.created || Date.now(), "yyyymmdd");
      const fileName = `救急搬送情報_${this.patient.name}_${dateStr}.pdf`;
      pdfUtil.createAndDownloadPdf(this.patient, fileName);
    } catch (error) {
      errorHandle(error, this);
    } finally {
      this.isBusy = false;
    }

    return;
  }
}
</script>

<style lang="scss" scoped>
#emergency-transport-patient-detail {
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  .register-card-box {
    border: 1px solid #ced4da;
    border-radius: 6px;
    background-color: #fff;
  }

  .cancel-btn,
  .print-btn {
    width: 120px;
  }
}
</style>
